import React, { useEffect, useRef, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import {
  PREVIEW_HANDOFF_MESSAGE_TYPE,
  PreviewHandoffMessage,
} from "@utils/previewLoginHandoff";

/** How often to check whether the login popup was closed before completing. */
const POPUP_POLL_INTERVAL_MS = 500;

/** The `window` event the popup login handoff is delivered on. */
const MESSAGE_EVENT = "message";

/**
 * Auth gate shown inside the Storyblok Visual Editor iframe.
 *
 * Okta's login page cannot render in the third-party iframe, so rather than redirecting we
 * open a top-level popup that completes the Okta login and `postMessage`s the tokens back
 * here (verified to work under storage partitioning in Chrome/Safari/Firefox). Once
 * authenticated, the previewed page renders and its draft API calls carry the Okta bearer
 * alongside the `_storyblok_tk` param. If the token later expires, `authState` flips back to
 * unauthenticated and this gate reappears for a one-click re-login.
 */
export const EditorPreviewLogin: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { oktaAuth, authState } = useOktaAuth();
  const popupRef = useRef<Window | null>(null);
  const nonceRef = useRef<string>("");
  const succeededRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // i18n key of the hint shown under the button (popup blocked / closed), or null.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const stopPolling = () => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Accept the handoff ONLY from our own origin, from the exact popup we opened, with our
      // nonce. The source check is mandatory — reject if we have no open popup.
      if (event.origin !== window.location.origin) return;
      if (!popupRef.current || event.source !== popupRef.current) return;
      const data = event.data as Partial<PreviewHandoffMessage> | undefined;
      if (data?.type !== PREVIEW_HANDOFF_MESSAGE_TYPE) return;
      if (!data.nonce || data.nonce !== nonceRef.current || !data.tokens) return;

      succeededRef.current = true;
      stopPolling();
      oktaAuth.tokenManager.setTokens(data.tokens);
      popupRef.current?.close();
      popupRef.current = null;
      nonceRef.current = "";
      setErrorKey(null);
    };
    window.addEventListener(MESSAGE_EVENT, onMessage);
    return () => {
      window.removeEventListener(MESSAGE_EVENT, onMessage);
      stopPolling();
    };
  }, [oktaAuth]);

  if (authState?.isAuthenticated) {
    return <>{children}</>;
  }

  const startLogin = () => {
    stopPolling();
    succeededRef.current = false;
    setErrorKey(null);
    const nonce = crypto.randomUUID();
    nonceRef.current = nonce;
    const popup = window.open(
      `${window.location.origin}/preview-login?nonce=${encodeURIComponent(nonce)}`,
      "pulse-preview-login",
      "popup,width=480,height=720",
    );
    if (!popup) {
      setErrorKey("EditorPreviewLogin.popupBlocked");
      return;
    }
    popupRef.current = popup;
    // If the user closes the popup before the handoff completes, surface a retry hint.
    pollRef.current = setInterval(() => {
      if (popupRef.current?.closed && !succeededRef.current) {
        stopPolling();
        popupRef.current = null;
        nonceRef.current = "";
        setErrorKey("EditorPreviewLogin.popupClosed");
      }
    }, POPUP_POLL_INTERVAL_MS);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center dark:bg-secondary-950">
      <Typography
        as="h1"
        size="2xl"
        className="text-secondary-700 dark:text-white"
      >
        {t("EditorPreviewLogin.title")}
      </Typography>
      <Typography
        size="lg"
        className="max-w-md text-pretty text-tertiary-700 dark:text-tertiary-100"
      >
        {t("EditorPreviewLogin.message")}
      </Typography>
      <Button
        id="editor-preview-sign-in"
        size="lg"
        variant="primary"
        onClick={startLogin}
      >
        {t("EditorPreviewLogin.signIn")}
      </Button>
      {errorKey && (
        <Typography
          size="sm"
          className="max-w-md text-pretty text-tertiary-500 dark:text-tertiary-400"
        >
          {t(errorKey)}
        </Typography>
      )}
    </div>
  );
};
